import React, { useState } from "react";
import { Link } from "react-router-dom";
import { PageHero } from "./pages";
import { LegalDocModal, useLegalModal } from "./legalDocs";
import {
  CheckCircle2, Mail, Send, MessageCircle, Linkedin, Download,
  ArrowUpRight, Plus, Minus, Shield, Globe, Users, Coins
} from "lucide-react";
import { useT } from "./i18n";

/* ═══════════════════════════ INVESTORS ═══════════════════════════ */
export function InvestorsPage() {
  const { t } = useT();
  return (
    <main data-testid="page-investors">
      <PageHero eyebrow="Seed+ Round" accent="gold"
        title={<>$1,000,000 Seed+ — <span className="accent">{t("pages2.568cd7")}</span></>}
        lead={t("pages2.240818")} />

      <section className="tg-page tg-section-tight">
        <div className="tg-bento">
          {[
            ["$300k", "RPA (Hong Kong Ltd.)", "Equity 3%"],
            ["$700k", "SAFT Token Agreement", "15M TGC @ $0.10"],
            ["12/24", t("pages2.0eedd4"), "Token warrant"],
            ["$100M", "FDV at TGE", "0.5% burn per tx"],
          ].map(([n, l, sub], i) => (
            <div key={i} className="tg-card">
              <div className="tg-stat-num blue">{n}</div>
              <div style={{ fontSize: 14, fontWeight: 700, marginTop: 8 }}>{l}</div>
              <div className="tg-stat-label">{sub}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="tg-page tg-section">
        <h2 className="tg-h2">{t("pages2.6dc6da")}</h2>
        <div className="tg-bento">
          {[
            { t: "Tranche 1 · $400k", m: "Closing", uses: [t("pages2.c1afbd"), "Smart-contract audit CertiK", t("pages2.e159e6")] },
            { t: "Tranche 2 · $350k", m: "Milestone: 50k users + TGE", uses: ["Wallester Visa production", "Sumsub KYC real", "Marketing + KOL"] },
            { t: "Tranche 3 · $250k", m: t("pages2.b196cd"), uses: ["Native iOS/Android", t("pages2.1af665"), t("pages2.62a0a9")] },
          ].map((tr, i) => (
            <div key={i} className="tg-card wide">
              <span className="tg-eyebrow gold">{tr.m}</span>
              <h3 className="tg-h3" style={{ marginTop: 12 }}>{tr.t}</h3>
              <ul style={{ padding: 0, listStyle: "none", margin: 0 }}>
                {tr.uses.map((u, k) => (
                  <li key={k} style={{ padding: "8px 0", color: "var(--tg-text-dim)", fontSize: 14, borderBottom: k < tr.uses.length - 1 ? "1px dashed var(--tg-border)" : "none" }}>
                    <CheckCircle2 size={14} color="#10F4A7" style={{ verticalAlign: "middle", marginRight: 8 }} /> {u}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="tg-page tg-section">
        <h2 className="tg-h2">{t("pages2.1d2b79")}</h2>
        <div className="tg-bento">
          {[
            ["🎟️", "Token warrant 15M TGC", t("pages2.117178")],
            ["📊", "Equity 3% Hong Kong Ltd.", t("pages2.be5af0")],
            ["🏛️", "Advisor / Board seat", t("pages2.0bfc1e")],
            ["🎁", "Early Access", t("pages2.8964cc")],
          ].map(([ic, t, d], i) => (
            <div key={i} className="tg-card wide">
              <div style={{ fontSize: 36, marginBottom: 10 }}>{ic}</div>
              <h3 className="tg-h3">{t}</h3>
              <p style={{ color: "var(--tg-text-dim)", fontSize: 14 }}>{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="tg-page tg-section">
        <div className="tg-card tg-card-glow" style={{ padding: 36, textAlign: "center" }}>
          <h2 className="tg-h2">{t("pages2.fd98f1")}</h2>
          <p className="tg-lead" style={{ margin: "0 auto 28px" }}>{t("pages2.a453f7")}</p>
          <div style={{ display: "inline-flex", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
            <a href="/api/investor" target="_blank" rel="noreferrer" className="tg-btn primary"><Download size={16} />{t("pages2.e5116e")}</a>
            <Link to="/contact" className="tg-btn ghost"><Mail size={16} />{t("pages2.1de1fc")}</Link>
          </div>
        </div>
      </section>
    </main>
  );
}

/* ═══════════════════════════ TOKENOMICS ═══════════════════════════ */
export function TokenomicsPage() {
  const { t } = useT();
  const alloc = [
    ["Public Sale / TGE", 20, "#00F2FF"],
    ["Liquidity Pool",    15, "#FFD700"],
    ["Ecosystem / Rewards", 20, "#10F4A7"],
    ["Team (vested)",     15, "#BC13FE"],
    ["Advisors / KOL",     5, "#FF3D6C"],
    ["Treasury / Reserve", 10, "#7C3AED"],
    ["Staking Rewards",    10, "#00B4FF"],
    ["Bear Charity",        5, "#F97316"],
  ];
  let cum = 0;
  return (
    <main data-testid="page-tokenomics">
      <PageHero eyebrow={t("pages2.1f69df")}
        title={<>1,000,000,000 TGC · <span className="accent">$100M FDV</span></>}
        lead={t("pages2.94c72e")} />

      <section className="tg-page tg-section-tight">
        <div className="tg-card" style={{ display: "flex", gap: 40, alignItems: "center", flexWrap: "wrap", justifyContent: "center" }}>
          {/* Donut */}
          <div className="tg-tok-donut">
            <svg viewBox="0 0 42 42" width="100%" height="100%" style={{ transform: "rotate(-90deg)" }}>
              <circle cx="21" cy="21" r="15.91549" fill="transparent" stroke="#1a1a22" strokeWidth="6" />
              {alloc.map(([label, pct, col], i) => {
                const dash = `${pct} ${100 - pct}`;
                const offset = 100 - cum;
                cum += pct;
                return <circle key={i} cx="21" cy="21" r="15.91549" fill="transparent"
                  stroke={col} strokeWidth="6" strokeDasharray={dash} strokeDashoffset={offset} />;
              })}
            </svg>
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
              <div className="tg-stat-num blue" style={{ fontSize: 28 }}>1B</div>
              <div className="tg-stat-label">TGC Total</div>
            </div>
          </div>
          {/* Legend */}
          <div className="tg-tok-legend" style={{ minWidth: 320, flex: 1 }}>
            {alloc.map(([label, pct, col], i) => (
              <div key={i} className="tg-tok-row">
                <span className="tg-tok-dot" style={{ background: col }} />
                <span style={{ flex: 1, fontSize: 14 }}>{label}</span>
                <b style={{ color: col }}>{pct}%</b>
                <span className="tg-stat-label" style={{ margin: 0, minWidth: 80, textAlign: "right" }}>
                  {(10 * pct).toFixed(0)}M TGC
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="tg-page tg-section">
        <h2 className="tg-h2">{t("pages2.c01f0a")}</h2>
        <div className="tg-card">
          <table className="tg-table">
            <thead><tr><th>{t("pages2.c95a1e")}</th><th>Cliff</th><th>Linear vesting</th><th>TGE unlock</th></tr></thead>
            <tbody>
              {[
                ["Team", t("pages2.ee94f1"), t("pages2.46724f"), "0%"],
                ["Advisors", t("pages2.5db104"), t("pages2.78636a"), "0%"],
                ["SAFT (seed)", t("pages2.ee94f1"), t("pages2.78636a"), "0%"],
                ["Public Sale", "—", "—", "100%"],
                ["Ecosystem / Rewards", "—", t("pages2.46724f"), "10%"],
                ["Liquidity", "—", "—", "100%"],
              ].map((row, i) => <tr key={i}>{row.map((c, j) => <td key={j}>{c}</td>)}</tr>)}
            </tbody>
          </table>
        </div>
      </section>

      <section className="tg-page tg-section">
        <h2 className="tg-h2">{t("pages2.83ddd0")}</h2>
        <div className="tg-bento">
          {[
            ["🔥", "0.5% burn per tx", t("pages2.801cde")],
            ["💎", "Staking lock-up", t("pages2.77abed")],
            ["🏛️", "Pension 5-year lock", t("pages2.a603e7")],
            ["⚡", "Energy Battery", t("pages2.497508")],
          ].map(([ic, t, d], i) => (
            <div key={i} className="tg-card">
              <div style={{ fontSize: 32, marginBottom: 10 }}>{ic}</div>
              <h3 className="tg-h3">{t}</h3>
              <p style={{ color: "var(--tg-text-dim)", fontSize: 13 }}>{d}</p>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}

/* ═══════════════════════════ ROADMAP ═══════════════════════════ */
export function RoadmapPage() {
  const { t } = useT();
  const phases = [
    { ph: "Q1 2026 · MVP", done: true, items: [
      "✅ Investor Pack (6 PDFs + Excel model)",
      t("pages2.4cd978"),
      t("pages2.4aeeda"),
      t("pages2.a289ba"),
    ]},
    { ph: "Q2 2026 · Launch", done: false, items: [
      "Smart-contract ERC-20 TGC + staking + referral",
      "CertiK + Hacken audit",
      t("pages2.98fd96"),
      "Native iOS + Android (React Native)",
      "Hong Kong VASP license + AIFC AFSA",
    ]},
    { ph: "Q3 2026 · Scale", done: false, items: [
      "Wallester Visa production integration",
      "Sumsub KYC real (video-ident)",
      "Binance API liquidity feed",
      t("pages2.824b09"),
      t("pages2.81893b"),
    ]},
    { ph: "Q4 2026 · Expansion", done: false, items: [
      t("pages2.764efb"),
      t("pages2.7e85dd"),
      "DAO governance (Snapshot + on-chain voting)",
      t("pages2.f9d423"),
    ]},
    { ph: "2027 · Global", done: false, items: [
      t("pages2.a29204"),
      t("pages2.e8cd21"),
      t("pages2.85af2e"),
      t("pages2.4f6bb5"),
    ]},
    { ph: "2028 · Billion Club", done: false, items: [
      t("pages2.598e91"),
      t("pages2.9dff12"),
      t("pages2.d67e9a"),
      t("pages2.389926"),
    ]},
  ];
  return (
    <main data-testid="page-roadmap">
      <PageHero eyebrow="Roadmap" title={<>{t("pages2.0c2f09")}<span className="accent">$1B cap</span>{t("pages2.7de0de")}</>}
        lead={t("pages2.bb1e20")} />
      <section className="tg-page tg-section-tight">
        <div className="tg-card" style={{ padding: 40 }}>
          {phases.map((p, i) => (
            <div key={i} className={`tg-road-item ${p.done ? "done" : ""}`}>
              <span className="tg-road-dot" />
              <div className="tg-road-phase">{p.ph}</div>
              <ul style={{ marginTop: 10, padding: 0, listStyle: "none" }}>
                {p.items.map((x, k) => (
                  <li key={k} style={{ padding: "4px 0", color: "var(--tg-text-dim)", fontSize: 15 }}>{x}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}

/* ═══════════════════════════ DOCUMENTS ═══════════════════════════ */
export function DocumentsPage() {
  const { t } = useT();
  const docs = [
    ["01_ONE_PAGER", "One-Pager (A4)", t("pages2.05aec1")],
    ["02_TOKENOMICS", "Tokenomics", t("pages2.b4fdf5")],
    ["03_PITCH_DECK", "Pitch Deck", t("pages2.d57d41")],
    ["04_INVESTMENT_MEMO", "Investment Memo", t("pages2.b71110")],
    ["05_TERM_SHEET", "Term Sheet", t("pages2.f9720d")],
    ["06_ROADMAP", "Roadmap", "Milestones Q1 2026 → Q4 2028"],
  ];
  return (
    <main data-testid="page-documents">
      <PageHero eyebrow={t("pages2.6d8365")} title={<>Investor Pack — <span className="accent">6 PDF + Excel model</span></>}
        lead={t("pages2.be8dcd")} />
      <section className="tg-page tg-section-tight">
        <div className="tg-bento">
          {docs.map(([f, t, d], i) => (
            <a key={i} href={`/api/investor/file/documents/${f}.pdf`} target="_blank" rel="noreferrer"
              className="tg-card wide" style={{ textDecoration: "none", color: "inherit" }}>
              <div className="tg-dropdown-icon" style={{ marginBottom: 14 }}>📄</div>
              <h3 className="tg-h3">{t}</h3>
              <p style={{ color: "var(--tg-text-dim)", fontSize: 14 }}>{d}</p>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--tg-blue)", fontSize: 13, fontWeight: 700, marginTop: 12 }}>{t("pages2.e546f7")}<ArrowUpRight size={14} />
              </div>
            </a>
          ))}
          <a href="/api/investor/file/financial_model/TGC_Financial_Model.xlsx" target="_blank" rel="noreferrer"
            className="tg-card full" style={{ textDecoration: "none", color: "inherit", background: "linear-gradient(135deg, rgba(255,215,0,0.1), transparent 60%)" }}>
            <div className="tg-dropdown-icon" style={{ marginBottom: 14, background: "rgba(255,215,0,0.1)", color: "var(--tg-gold)", borderColor: "rgba(255,215,0,0.35)" }}>📊</div>
            <h3 className="tg-h3">Excel Financial Model</h3>
            <p style={{ color: "var(--tg-text-dim)", fontSize: 14 }}>{t("pages2.692324")}</p>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--tg-gold)", fontSize: 13, fontWeight: 700, marginTop: 12 }}>{t("pages2.b0efac")}<ArrowUpRight size={14} />
            </div>
          </a>
        </div>
      </section>
    </main>
  );
}

/* ═══════════════════════════ TEAM ═══════════════════════════ */
export function TeamPage() {
  const { t } = useT();
  return (
    <main data-testid="page-team">
      <PageHero eyebrow={t("header.company.team.title")} title={<>{t("pages2.461d1f")}<span className="accent">Sergey Elfenbein</span></>}
        lead={t("pages2.432e41")} />
      <section className="tg-page tg-section-tight">
        <div className="tg-bento">
          <div className="tg-card full" style={{ display: "flex", gap: 32, alignItems: "center", flexWrap: "wrap" }}>
            <img src="/api/investor/file/assets/founder_headshot_v1.png" alt="Sergey Elfenbein"
              style={{ width: 180, height: 180, borderRadius: 90, objectFit: "cover", border: "3px solid var(--tg-gold)", boxShadow: "0 0 60px rgba(255,215,0,0.3)" }} />
            <div style={{ flex: 1, minWidth: 260 }}>
              <div className="tg-eyebrow gold">Founder · CEO</div>
              <h3 className="tg-h2" style={{ margin: "12px 0" }}>Sergey Elfenbein</h3>
              <p style={{ color: "var(--tg-text-dim)", fontSize: 15, lineHeight: 1.6 }}>{t("pages2.dc938b")}</p>
              <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
                <a href="https://www.linkedin.com/in/sergey-elfenbein-8224631a4/" target="_blank" rel="noreferrer" className="tg-btn ghost"><Linkedin size={14} /> LinkedIn</a>
                <a href="https://t.me/elfenbeinsergey" target="_blank" rel="noreferrer" className="tg-btn ghost"><Send size={14} /> Telegram</a>
                <a href="mailto:elfenbeinsergey@gmail.com" className="tg-btn ghost"><Mail size={14} /> Email</a>
              </div>
            </div>
          </div>
          {[
            ["👨‍💻", t("pages2.206c75"), t("pages2.216629")],
            ["⚖️", "General Counsel", t("pages2.4d1c28")],
            ["📈", "CFO", t("pages2.ae94ba")],
            ["🎨", "Head of Product", t("pages2.c3bbab")],
          ].map(([ic, t, d], i) => (
            <div key={i} className="tg-card">
              <div style={{ fontSize: 36, marginBottom: 10 }}>{ic}</div>
              <h3 className="tg-h3">{t}</h3>
              <p style={{ color: "var(--tg-text-dim)", fontSize: 13 }}>{d}</p>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}

/* ═══════════════════════════ FAQ ═══════════════════════════ */
const QUESTION_KEYS = [
  ["pages2.q1.q", "pages2.q1.a"],
  ["pages2.q2.q", "pages2.q2.a"],
  ["pages2.q3.q", "pages2.q3.a"],
  ["pages2.q4.q", "pages2.q4.a"],
  ["pages2.q5.q", "pages2.q5.a"],
  ["pages2.q6.q", "pages2.q6.a"],
  ["pages2.q7.q", "pages2.q7.a"],
  ["pages2.q8.q", "pages2.q8.a"],
  ["pages2.q9.q", "pages2.q9.a"],
  ["pages2.q10.q", "pages2.q10.a"],
];
export function FAQPage() {
  const { t } = useT();
  const [open, setOpen] = useState(0);
  const QUESTIONS = QUESTION_KEYS.map(([qk, ak]) => [t(qk), t(ak)]);
  return (
    <main data-testid="page-faq">
      <PageHero eyebrow="FAQ" title={<>{t("pages2.61dc87")}<span className="accent">{t("pages2.fadd15")}</span></>} />
      <section className="tg-page tg-section-tight">
        <div className="tg-card">
          {QUESTIONS.map(([q, a], i) => (
            <div key={i} className="tg-faq-item">
              <div className="tg-faq-q" onClick={() => setOpen(open === i ? -1 : i)} data-testid={`faq-q-${i}`}>
                <span>{q}</span>
                {open === i ? <Minus size={20} /> : <Plus size={20} />}
              </div>
              {open === i && <div className="tg-faq-a">{a}</div>}
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}

/* ═══════════════════════════ LEGAL ═══════════════════════════ */
export function LegalPage() {
  const { t } = useT();
  const { openDoc, setOpenDoc, close } = useLegalModal();
  const ReadBtn = ({ k }) => (
    <button onClick={() => setOpenDoc(k)} data-testid={`legal-read-${k}`}
      style={{ background: "transparent", border: "none", color: "var(--tg-blue)", cursor: "pointer", fontWeight: 600, padding: 0, fontSize: 14 }}>{t("pages2.c6fbf4")}</button>
  );
  return (
    <main data-testid="page-legal">
      <PageHero eyebrow="Legal" title={<>{t("pages2.ec9ddd")}<span className="accent">compliance</span></>}
        lead={t("pages2.33bbfa")} />
      <section className="tg-page tg-section-tight">
        <div className="tg-bento">
          {[
            ["🏢 Corporate entity", "Turbo Global Coin Ltd. (Hong Kong), Reg. No. xxx-xxx-xxx. Central Registry: pending Q2 2026."],
            ["📜 VASP / VCA license", t("pages2.6fff31")],
            ["⚖️ Securities", t("pages2.46862c")],
            ["🛡️ AML / KYC", t("pages2.7701cc")],
            ["🇪🇺 GDPR", t("pages2.6b9c3b")],
            ["🇺🇸 US Persons", t("pages2.a4c783")],
          ].map(([t, d], i) => (
            <div key={i} className="tg-card wide">
              <h3 className="tg-h3">{t}</h3>
              <p style={{ color: "var(--tg-text-dim)", fontSize: 14 }}>{d}</p>
            </div>
          ))}
        </div>
      </section>
      <section className="tg-page tg-section">
        <h2 className="tg-h2">{t("pages2.6d8365")}</h2>
        <div className="tg-card">
          <table className="tg-table">
            <thead><tr><th>{t("pages2.383135")}</th><th>{t("pages2.372521")}</th><th>{t("pages2.8cdd8b")}</th><th>{t("pages2.50a761")}</th></tr></thead>
            <tbody>
              <tr><td>Terms of Service</td><td>v1.0</td><td>01.01.2026</td><td><ReadBtn k="tos" /></td></tr>
              <tr><td>Privacy Policy (GDPR)</td><td>v1.0</td><td>01.01.2026</td><td><ReadBtn k="privacy" /></td></tr>
              <tr><td>Token Purchase Agreement (SAFT)</td><td>v2.1</td><td>15.03.2026</td><td><ReadBtn k="saft" /></td></tr>
              <tr><td>Cookie Policy</td><td>v1.0</td><td>01.01.2026</td><td><ReadBtn k="cookie" /></td></tr>
              <tr><td>AML Disclosure</td><td>v1.0</td><td>01.01.2026</td><td><ReadBtn k="aml" /></td></tr>
            </tbody>
          </table>
        </div>
      </section>
      {openDoc && <LegalDocModal docKey={openDoc} onClose={close} />}
    </main>
  );
}

/* ═══════════════════════════ CONTACT ═══════════════════════════ */
export function ContactPage() {
  const { t } = useT();
  const [form, setForm] = useState({ name: "", email: "", company: "", amount: "", message: "" });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setSubmitting(true);
    try {
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const r = await fetch(`${backend}/api/tgc/investor-leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `Ошибка ${r.status}`);
      }
      setSent(true);
    } catch (e2) {
      setErr(typeof e2.message === "string" ? e2.message : "Не удалось отправить. Попробуйте позже.");
    } finally { setSubmitting(false); }
  };

  return (
    <main data-testid="page-contact">
      <PageHero eyebrow={t("header.company.contact.title")} title={<>{t("pages2.6b0828")}<span className="accent">{t("pages2.9d09ad")}</span></>}
        lead={t("pages2.45f677")} />

      <section className="tg-page tg-section-tight">
        <div className="tg-bento">
          {[
            { icon: <Mail size={22} />,          t: "Email", href: "mailto:elfenbeinsergey@gmail.com", v: "elfenbeinsergey@gmail.com" },
            { icon: <Send size={22} />,          t: "Telegram", href: "https://t.me/elfenbeinsergey", v: "@elfenbeinsergey" },
            { icon: <Linkedin size={22} />,      t: "LinkedIn", href: "https://www.linkedin.com/in/sergey-elfenbein-8224631a4/", v: "Sergey Elfenbein" },
          ].map((c, i) => (
            <a key={i} href={c.href} target="_blank" rel="noreferrer" className="tg-card wide" style={{ textDecoration: "none", color: "inherit" }}>
              <div className="tg-dropdown-icon" style={{ marginBottom: 14 }}>{c.icon}</div>
              <h3 className="tg-h3">{c.t}</h3>
              <div style={{ color: "var(--tg-blue)", fontSize: 14, fontWeight: 600 }}>{c.v}</div>
            </a>
          ))}
        </div>
      </section>

      <section className="tg-page tg-section">
        <div className="tg-card" style={{ maxWidth: 720, margin: "0 auto", padding: 36 }}>
          <h2 className="tg-h2" style={{ marginBottom: 8 }}>{t("pages2.4b83cf")}</h2>
          <p style={{ color: "var(--tg-text-dim)", marginBottom: 24, fontSize: 14 }}>{t("pages2.0d4d61")}</p>
          {sent ? (
            <div style={{ padding: 32, textAlign: "center", background: "rgba(16,244,167,0.08)", borderRadius: 16, border: "1px solid rgba(16,244,167,0.3)" }}>
              <CheckCircle2 size={48} color="#10F4A7" />
              <h3 className="tg-h3" style={{ marginTop: 14 }}>{t("pages2.15b47a")}</h3>
              <p style={{ color: "var(--tg-text-dim)" }}>{t("pages2.8ed7c2")}<b>{form.email}</b></p>
            </div>
          ) : (
            <form onSubmit={submit} style={{ display: "grid", gap: 14 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <input required placeholder={t("pages2.8e7692")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  style={inputStyle} data-testid="contact-name" />
                <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  style={inputStyle} data-testid="contact-email" />
              </div>
              <input placeholder={t("pages2.0b8146")} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
                style={inputStyle} data-testid="contact-company" />
              <select value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                style={inputStyle} data-testid="contact-amount">
                <option value="">{t("pages2.e1ae18")}</option>
                <option>$50k – $100k</option>
                <option>$100k – $250k</option>
                <option>$250k – $500k</option>
                <option>$500k – $1M (full round)</option>
                <option>$1M+ (strategic)</option>
              </select>
              <textarea rows={5} placeholder={t("pages2.f1ae79")} value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                style={{ ...inputStyle, resize: "vertical", minHeight: 120 }} data-testid="contact-message" />
              {err && <div style={{ color: "#FF3D6C", fontSize: 13, textAlign: "center" }} data-testid="contact-error">{err}</div>}
              <button type="submit" className="tg-btn primary" disabled={submitting} style={{ justifyContent: "center", opacity: submitting ? 0.6 : 1 }} data-testid="contact-submit">
                <Send size={16} />{submitting ? "Отправка…" : t("pages2.420695")}</button>
              <p style={{ color: "var(--tg-text-hint)", fontSize: 12, textAlign: "center", margin: 0 }}>{t("pages2.3984c0")}<Link to="/legal" style={{ color: "var(--tg-blue)" }}>Privacy Policy</Link>
              </p>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}
const inputStyle = {
  width: "100%", padding: "14px 16px", background: "rgba(255,255,255,0.03)",
  border: "1px solid var(--tg-border)", borderRadius: 12, color: "var(--tg-text)",
  fontSize: 14, fontFamily: "inherit", outline: "none"
};
